import { useState } from 'react';
import type { FieldType } from '@jfh/intake-shared';
import { Icon } from '../../components/Icon.js';
import { Modal } from '../../components/Modal.js';
import { pickerGroups } from './logic.js';

export interface QuestionPickerProps {
  open: boolean;
  /** Where the question will go, in words. */
  where: string;
  staffView: boolean;
  onPick: (type: FieldType) => void;
  onClose: () => void;
}

/** "Add a question" (F9 P3): every type by its plain name, with a description and an example. */
export function QuestionPicker({ open, where, staffView, onPick, onClose }: QuestionPickerProps) {
  const [query, setQuery] = useState('');
  const groups = pickerGroups(query, staffView);
  const close = () => {
    setQuery('');
    onClose();
  };

  return (
    <Modal open={open} onClose={close} width={760}>
      <div role="dialog" aria-modal="true" aria-label="Add a question" style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 90px)' }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Add a question</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>{where}</div>
            </div>
            <button type="button" className="icon-btn" onClick={close} aria-label="Close">
              <Icon name="x" size={16} />
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-3)' }}>
              <Icon name="search" size={15} />
            </span>
            <input
              className="input"
              autoFocus
              style={{ paddingLeft: 34 }}
              placeholder="What do you want to ask? e.g. phone, VAT, upload"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                const first = groups[0]?.items[0];
                if (e.key === 'Enter' && first) {
                  setQuery('');
                  onPick(first.type);
                }
              }}
              aria-label="Search question types"
            />
          </div>
        </div>
        <div style={{ overflowY: 'auto', padding: '14px 20px 20px', display: 'grid', gap: 16 }}>
          {groups.map((g) => (
            <div key={g.label}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '.12em',
                  color: 'var(--jfh-cactus)',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                {g.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                {g.items.map((t) => (
                  <button
                    key={t.type}
                    type="button"
                    className="palette-item"
                    onClick={() => {
                      setQuery('');
                      onPick(t.type);
                    }}
                    style={{ alignItems: 'flex-start', padding: '10px 12px', gap: 10, height: 'auto', textAlign: 'left' }}
                  >
                    <span
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 'var(--r-md)',
                        background: 'var(--accent-soft)',
                        color: 'var(--accent-text)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 'none',
                      }}
                    >
                      <Icon name={t.icon} size={15} />
                    </span>
                    <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-1)' }}>
                        {t.type === 'heading' ? 'New step' : t.plainName}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.35, fontWeight: 500 }}>
                        {t.description}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic', fontWeight: 500 }}>
                        e.g. {t.example}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: 24, fontSize: 13 }}>
              Nothing matches. Try “text”, “list” or “address”.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
